<script setup>
    import { useDisclosureCounselingDetailStore } from '@/stores/modal/disclosureCounselingDetailStore';
    import { useCommonStore } from '@/stores/common/commonStore';
    import { defineEmits, defineExpose, inject, ref, computed } from 'vue';
    import { useRouter } from 'vue-router';
    import { useButtonDisabledStore } from '@/stores/common/disabledStore';

    // メッセージダイアログ取得。
    const { messageDialog } = inject('messageDialogKey');

    const router = useRouter();

    // ストア取得
    const commonStore = useCommonStore();
    const disclosureCounselingDetailStore = useDisclosureCounselingDetailStore();

    // 開示カウンセリング詳細モーダルの表示・非表示を管理する。
    const disclosureCounselingDetailDispFlg = ref('');
    // 開示カウンセリング取消確認モーダルの表示・非表示を管理する。
    const disclosureCounselingCancelConfirmDispFlg = ref('');
    // 開示カウンセリング取消完了モーダルの表示・非表示を管理る。
    const disclosureCounselingCancelCompleteDispFlg = ref('');

    // 親画面から取得したAPIのドメイン部を管理する。
    const API_URL = ref('');

    // ダブルクリック対策
    const isPageDoubleClickBlocked = ref(false);

    // 呼び出し元の親画面の開示カウンセリング詳細モーダルを閉じる処理を定義する。
    const emit = defineEmits(['disclosureCounselingDetailModal:close']);

    // ボタン連打ガード制御
    const disabledStore = useButtonDisabledStore();

    /**
     * 開示カウンセリング詳細データ取得処理
     */
    async function getDisclosureCounselingDetail() {
        const queryParam = {
            SessionGrpPk: disclosureCounselingDetailStore.SessionGrpPk,
        };

        const response = await disclosureCounselingDetailStore.getDisclosureCounselingDetailAction(API_URL.value, queryParam);

        if (!response.success) {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
            return;
        }
    }

    /**
     * 開示カウンセリング取消確認モーダルを表示する。
     */
    async function cancelConfirmModal() {
        disclosureCounselingDetailDispFlg.value = '';
        disclosureCounselingCancelConfirmDispFlg.value = 'block';
        disclosureCounselingCancelCompleteDispFlg.value = '';
    }

    /**
     * 開示カウンセリング取消確認モーダルから開示カウンセリング詳細モーダルに戻る。
     */
    function backToDetailModal() {
        disclosureCounselingDetailDispFlg.value = 'block';
        disclosureCounselingCancelConfirmDispFlg.value = '';
        disclosureCounselingCancelCompleteDispFlg.value = '';
    }

    /**
     * 開示カウンセリング取消を実行し、開示カウンセリング取消完了モーダルを表示する。
     */
    async function cancelCompleteModal() {
        // ダブルクリックを無効化する。
        if (isPageDoubleClickBlocked.value) {
            return;
        }

        // ボタン連打ガード制御
        disabledStore.buttonTemporaryDisabled();

        // ダブルクリック対策フラグを有効にする。
        isPageDoubleClickBlocked.value = true;

        const queryParam = {
            SessionGrpPk: disclosureCounselingDetailStore.SessionGrpPk,
        };
        const response = await disclosureCounselingDetailStore.deleteDisclosureCounselingAction(API_URL.value, queryParam);
        if (response.success) {
            disclosureCounselingDetailDispFlg.value = '';
            disclosureCounselingCancelConfirmDispFlg.value = '';
            disclosureCounselingCancelCompleteDispFlg.value = 'block';
            reset();
        } else {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
        }

        // ダブルクリック対策フラグを初期化する。
        isPageDoubleClickBlocked.value = false;
    }

    /**
     * 開示カウンセリング詳細モーダルの「戻る」ボタン、開示カウンセリング取消完了モーダルの「閉じる」ボタンの共通ボタンイベント。
     * 親画面に戻る。
     */
    function closeDisclosureCounselingModal() {
        // 親画面のキャンセル処理を呼び出す。
        emit('disclosureCounselingDetailModal:close');
    }

    /**
     * 親画面から開示カウンセリング詳細モーダルを開くイベントが実行された場合の処理。
     * @param {String} trg HTMLファイルタグが生成されている座標を取得する。
     * @param {String} sessionGroupPk 対象の開示カウンセリング詳細を取得するためのセッショングループPK
     * @returns なし
     */
    async function openModal(trg, sessionGroupPk) {
        // 変数を初期化する。
        reset();

        // モーダルの表示を初期化する。詳細モーダルの表示に切り替えて、他モーダルは全て閉じる。
        disclosureCounselingDetailDispFlg.value = 'block';
        disclosureCounselingCancelConfirmDispFlg.value = '';
        disclosureCounselingCancelCompleteDispFlg.value = '';

        // APIのURL情報を取得する。
        API_URL.value = trg;
        disclosureCounselingDetailStore.SessionGrpPk = sessionGroupPk;

        await getDisclosureCounselingDetail();

        // モーダルのHTML要素を取得する。
        const elm1 = document.querySelector('#modalOpenCounselingDetail .modalContents');

        // モーダルの縦スクロールを先頭に戻す。
        elm1.scrollTop = 0;
    }

    /**
     * YYYY/MM/DD hh:mm:ssをYYYY/MM/DDとして返す。
     * @param {String} date YYYY/MM/DD hh:mm:ss
     */
    function DateDisp(date) {
        if (date) {
            return date.split(' ')[0];
        }
    }

    function reportOpen(reservationPk) {
        let resolvedRoute = router.resolve({ name: 'DisclosureCounselingReport', params: { sessionGrpPk: disclosureCounselingDetailStore.SessionGrpPk, reservationPk: reservationPk } });
        window.open(resolvedRoute.href, '_blank');
    }

    const disabled = computed(() => {
        return !disclosureCounselingDetailStore.disclosureCounselingDetail.AllowCansel;
    });

    /**
     * 初期化する。
     */
    function reset() {
        disclosureCounselingDetailStore.reset();

        // ダブルクリック対策フラグを初期化する。
        isPageDoubleClickBlocked.value = false;
    }

    /**
     * 親画面から実行を許可する本コンポーネント内のメソッドの一覧。
     */
    defineExpose({
        openModal,
    });
</script>
<template>
    <article id="modalOpenCounselingDetail" class="modal" :style="{ display: disclosureCounselingDetailDispFlg }">
        <h3 class="textCenter">{{ $t('disclosureCounseling.parts.pageTitle1') }}</h3>
        <section class="modalContents" style="height: calc(100% - 145px)">
            <section class="form">
                <!-- 管理番号 -->
                <dl>
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('disclosureCounseling.parts.tableHeader2') }}</label>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li>
                                <span>{{ disclosureCounselingDetailStore.disclosureCounselingDetail.SessionGrpNo }}</span>
                            </li>
                        </ul>
                    </dd>
                </dl>
                <!-- ステータス -->
                <dl>
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('disclosureCounseling.parts.tableHeader5') }}</label>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li>
                                <span>{{ commonStore.getConvertDictionary('SessionGrpStatus', disclosureCounselingDetailStore.disclosureCounselingDetail.SessionGrpStatus) }}</span>
                            </li>
                        </ul>
                    </dd>
                </dl>
                <!-- 申込日 -->
                <dl>
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('disclosureCounseling.parts.tableHeader1') }}</label>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li>
                                <span>{{ DateDisp(disclosureCounselingDetailStore.disclosureCounselingDetail.ApplyDt) }}</span>
                            </li>
                        </ul>
                    </dd>
                </dl>
                <!-- 申込者 -->
                <dl>
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('disclosureCounseling.parts.dt1') }}</label>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li>
                                <span>{{ disclosureCounselingDetailStore.disclosureCounselingDetail.ApplicantNm }}</span>
                            </li>
                        </ul>
                    </dd>
                </dl>
                <!-- サービス -->
                <dl>
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('disclosureCounseling.parts.tableHeader3') }}</label>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li>
                                <span>{{ disclosureCounselingDetailStore.disclosureCounselingDetail.ServiceNm }}</span>
                            </li>
                        </ul>
                    </dd>
                </dl>
                <!-- 対象者 -->
                <dl>
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('disclosureCounseling.parts.tableHeader4') }}</label>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li>
                                <span>{{ disclosureCounselingDetailStore.disclosureCounselingDetail.ClientNm }}</span>
                            </li>
                        </ul>
                    </dd>
                </dl>
                <!-- 報告先 -->
                <dl v-if="disclosureCounselingDetailStore.disclosureCounselingDetail.ReportingUsers.length !== 0">
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('disclosureCounseling.parts.dt2') }}</label>
                            </li>
                        </ul>
                    </dt>
                    <dd style="margin-top: 10px; margin-bottom: 10px">
                        <ul class="formList">
                            <li v-for="(user, index) in disclosureCounselingDetailStore.disclosureCounselingDetail.ReportingUsers" :key="index">
                                {{ user }}
                            </li>
                        </ul>
                    </dd>
                </dl>
                <!-- 連絡 -->
                <dl>
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('disclosureCounseling.parts.dt3') }}</label>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li>
                                <p>{{ disclosureCounselingDetailStore.disclosureCounselingDetail.SessionGrpApplyNote }}</p>
                            </li>
                        </ul>
                    </dd>
                </dl>
                <!-- カウンセラー -->
                <dl>
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('disclosureCounseling.parts.tableHeader6') }}</label>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li>
                                <span>{{ disclosureCounselingDetailStore.disclosureCounselingDetail.CounselorNm }}</span>
                            </li>
                        </ul>
                    </dd>
                </dl>
                <!-- 予約期間 -->
                <dl>
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('disclosureCounseling.parts.tableHeader7') }}</label>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li>
                                <span>{{ disclosureCounselingDetailStore.disclosureCounselingDetail.SessionGrpRsvDateS }} - {{ disclosureCounselingDetailStore.disclosureCounselingDetail.SessionGrpRsvDateE }}</span>
                            </li>
                        </ul>
                    </dd>
                </dl>
                <!-- 報告書一覧 -->
                <dl>
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('disclosureCounseling.parts.dt4') }}</label>
                            </li>
                        </ul>
                    </dt>
                    <dd style="margin-top: 10px">
                        <section class="innerTable" style="height: auto">
                            <table class="table">
                                <thead>
                                    <tr>
                                        <th>
                                            <!--ステータス-->
                                            <span>{{ $t('disclosureCounseling.parts.tableHeader5') }}</span>
                                        </th>
                                        <th>
                                            <!--予約日時-->
                                            <span>{{ $t('disclosureCounseling.parts.subTableHeader1') }}</span>
                                        </th>
                                        <th>
                                            <!--担当カウンセラー-->
                                            <span>{{ $t('disclosureCounseling.parts.subTableHeader2') }}</span>
                                        </th>
                                        <th>
                                            <!--報告書-->
                                            <span>{{ $t('disclosureCounseling.parts.subTableHeader3') }}</span>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(reserve, index) in disclosureCounselingDetailStore.disclosureCounselingDetail.ReserveList" :key="index">
                                        <td>
                                            <span>{{ commonStore.getConvertDictionary('DisclosuerCounselingDetailReportReservationStatus', reserve.ReservationStatus) }}</span>
                                        </td>
                                        <td>
                                            <span>{{ DateDisp(reserve.ReservationDt) }}</span>
                                        </td>
                                        <td>
                                            <span>{{ reserve.CounselorNm }}</span>
                                        </td>
                                        <td>
                                            <button v-if="reserve.ReportOutput" @click="reportOpen(reserve.ReservationPk)" class="btnReport">{{ $t('disclosureCounseling.parts.button2') }}</button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </section>
                    </dd>
                </dl>
            </section>
        </section>
        <section class="modalFooter">
            <ul class="btnArea center">
                <li>
                    <!--戻る-->
                    <button type="button" class="btnNormal closeModal" @click="closeDisclosureCounselingModal">{{ $t('common.parts.backPage') }}</button>
                </li>
                <li>
                    <!--取り消し-->
                    <button type="button" class="btnPrimary" id="goToModalOpenCounselingCancelConfirm" @click="cancelConfirmModal" :disabled="disabled">{{ $t('disclosureCounseling.parts.button3') }}</button>
                </li>
            </ul>
        </section>
    </article>
    <!--開示カウンセリング詳細-->
    <article id="modalOpenCounselingCancelConfirm" class="modal small" :style="{ display: disclosureCounselingCancelConfirmDispFlg }">
        <p class="textCenter" style="padding-left: 80px; width: 285px; margin-bottom: 21px">{{ $t('disclosureCounseling.parts.modalTitle1') }}</p>
        <section class="modalFooter">
            <ul class="btnArea center">
                <li>
                    <button type="button" class="btnNormal" style="width: 180px" id="backToModalOpenCounselingDetail" @click="backToDetailModal">{{ $t('disclosureCounseling.parts.modalButton2') }}</button>
                </li>
                <li>
                    <button type="button" class="btnPrimary" style="width: 180px" id="goToModalOpenCounselingCancelComplete" @click="cancelCompleteModal" :disabled="disabledStore.isDisabled">{{ $t('disclosureCounseling.parts.modalButton1') }}</button>
                </li>
            </ul>
        </section>
    </article>
    <!--開示カウンセリング申込取消確認-->
    <article id="modalOpenCounselingCancelComplete" class="modal small" :style="{ display: disclosureCounselingCancelCompleteDispFlg }">
        <p class="textCenter" style="padding-left: 80px; width: 285px; margin-bottom: 21px">{{ $t('disclosureCounseling.parts.modalTitle2') }}</p>
        <section class="modalFooter">
            <ul class="btnArea center">
                <li>
                    <button type="button" class="btnNormal closeModal" @click="closeDisclosureCounselingModal">{{ $t('disclosureCounseling.parts.modalButton3') }}</button>
                </li>
            </ul>
        </section>
    </article>
    <!--開示カウンセリング申込取消完了-->
</template>

<style></style>
